import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Archive, ClipboardCopy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { mirrorIrSweep, mirrorIrSweepPreview } from "@/lib/api";

const STATUS_COL = "status";

/** Per-status counts for a chase group, in ladder order (then the terminal status, then anything else). */
export const stageCounts = (group, cfg) => {
  const counts = new Map();
  (group.items || []).forEach(it => { const s = (it.cols || {})[STATUS_COL] || "(blank)"; counts.set(s, (counts.get(s) || 0) + 1); });
  const ladder = Object.keys(cfg?.stages || {});
  const terminal = Object.values(cfg?.stages || {}).find(s => s.danger)?.next;
  const ordered = [...ladder, ...(terminal ? [terminal] : [])].filter(s => counts.has(s));
  const rest = [...counts.keys()].filter(s => !ordered.includes(s)).sort();
  return [...ordered, ...rest].map(s => ({ status: s, count: counts.get(s), terminal: s === terminal }));
};

export function StageChips({ group, cfg, filter, onFilter }) {
  const rows = useMemo(() => stageCounts(group, cfg), [group, cfg]);
  const total = (group.items || []).length;
  const copy = async () => {
    const stamp = new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const lines = [`${group.title} — ${stamp}`, ...rows.map(r => `${r.status}${r.terminal ? " (to file)" : ""}: ${r.count}`), `Total: ${total}`];
    try { await navigator.clipboard.writeText(lines.join("\n")); toast.success(`${group.title} numbers copied`); }
    catch { toast.error("Couldn't copy — clipboard blocked"); }
  };
  if (!rows.length) return null;
  const chip = (label, count, active, onClick, tone, testId) => (
    <button key={label} type="button" onClick={onClick}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition-colors",
        active ? "bg-slate-800 text-white border-slate-800" : cn("bg-white hover:bg-slate-100 text-slate-700 border-slate-200", tone))}
      data-testid={testId}>
      <span>{label}</span><span className={cn("font-semibold tabular-nums", active ? "text-white" : "text-slate-900")}>{count}</span>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2 -mt-0.5" onClick={(e) => e.stopPropagation()} data-testid={`mirror-stage-chips-${group.id}`}>
      {chip("All", total, !filter, () => onFilter(null), "", `mirror-stage-chip-all-${group.id}`)}
      {rows.map(r => chip(r.status, r.count, filter === r.status, () => onFilter(filter === r.status ? null : r.status),
        r.terminal ? "border-red-200 text-red-700" : "", `mirror-stage-chip-${group.id}-${r.status.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`))}
      <button type="button" onClick={copy} className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-900"
        title="Copy the per-stage numbers for the report" data-testid={`mirror-copy-numbers-${group.id}`}>
        <ClipboardCopy className="w-3 h-3" />Copy numbers
      </button>
    </div>
  );
}

/** "File declined IRs" — preview + confirm; status-Declined rows ticked, overdue final-chase rows unticked by default. */
export function IRSweepButton({ count, actor, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-red-200 text-red-700 hover:bg-red-50"
        onClick={() => setOpen(true)} data-testid="mirror-ir-sweep-btn">
        <Archive className="w-3 h-3 mr-1" />File declined{count ? ` (${count})` : ""}
      </Button>
      {open && <IRSweepDialog actor={actor} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />}
    </>
  );
}

function IRSweepDialog({ actor, onClose, onDone }) {
  const [days, setDays] = useState(2);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticked, setTicked] = useState(() => new Set());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    mirrorIrSweepPreview(days).then(p => {
      if (!alive) return;
      setPreview(p);
      setTicked(prev => { const n = new Set([...prev].filter(id => p.overdue.some(r => r.id === id))); p.declined.forEach(r => n.add(r.id)); return n; });
    }).catch(e => toast.error(e.response?.data?.detail || e.message)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days]);

  const toggle = (id) => setTicked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tickAll = (rows, on) => setTicked(prev => { const n = new Set(prev); rows.forEach(r => on ? n.add(r.id) : n.delete(r.id)); return n; });
  const run = async () => {
    setRunning(true);
    try {
      const r = await mirrorIrSweep([...ticked], actor, days);
      setResult(r);
      toast[r.failed ? "warning" : "success"](`${r.filed} filed under ${preview?.target_group || "Declined/Finished"}${r.failed ? ` — ${r.failed} skipped` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setRunning(false); }
  };

  const Row = ({ r, hold }) => (
    <label className={cn("flex items-center gap-2 px-2.5 py-1.5 text-[11px]", hold ? "text-slate-400" : "cursor-pointer hover:bg-slate-50")}
      data-testid={`mirror-ir-sweep-row-${r.id}`}>
      <input type="checkbox" checked={ticked.has(r.id)} disabled={!!hold || running || !!result} onChange={() => toggle(r.id)} />
      <span className="flex-1 truncate font-medium">{r.name}</span>
      {r.reason && <span className="text-[9px] text-slate-500 truncate max-w-[140px]" title={`Reason for decline already set: ${r.reason}`}>{r.reason}</span>}
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
        {hold || (r.business_days != null ? `${r.business_days} bus. day${r.business_days === 1 ? "" : "s"} since final chase` : r.last_chase ? `chased ${r.last_chase}` : "")}
      </span>
      {result && (() => { const x = result.results.find(x => x.id === r.id); return x ? <span className={x.ok ? "text-green-600" : "text-red-600"} title={x.error || ""}>{x.ok ? "✓" : "✗"}</span> : null; })()}
    </label>
  );

  const declined = preview?.declined || [], overdue = preview?.overdue || [], waiting = preview?.waiting || [];
  return (
    <AlertDialog open onOpenChange={(o) => { if (!o && !running) (result ? onDone() : onClose()); }}>
      <AlertDialogContent className="max-w-2xl" data-testid="mirror-ir-sweep-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><Archive className="w-4 h-4 text-red-600" />File declined IRs</AlertDialogTitle>
          <AlertDialogDescription>
            Ticked rows move to <b>{preview?.target_group || "Declined/Finished"}</b>; a blank Reason for decline is set to <b>{preview?.reason || "Info Requested Not Received"}</b> and a note is left on the item. No emails are sent.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {loading && !preview ? (
          <div className="py-8 text-center text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Reading the Information Requested group…</div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <section>
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700 mb-1">
                <span>Already declined, still in this group ({declined.length})</span>
                {declined.length > 0 && !result && <button type="button" className="text-[10px] text-blue-600 hover:underline" onClick={() => tickAll(declined, !declined.every(r => ticked.has(r.id)))} data-testid="mirror-ir-sweep-toggle-declined">{declined.every(r => ticked.has(r.id)) ? "untick all" : "tick all"}</button>}
              </div>
              <div className="border rounded divide-y">{declined.length ? declined.map(r => <Row key={r.id} r={r} />) : <div className="p-2 text-[11px] text-muted-foreground">None — the group is clean.</div>}</div>
            </section>
            <section>
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700 mb-1 gap-2">
                <span className="flex items-center gap-1.5">Final chase sent, no reply for
                  <input type="number" min={0} max={30} value={days} disabled={running || !!result} onChange={(e) => setDays(Math.max(0, Math.min(30, Number(e.target.value) || 0)))}
                    className="h-5 w-10 text-[10px] border rounded px-1 text-center" data-testid="mirror-ir-sweep-days-input" />
                  business days — decline + file ({overdue.length}){loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                </span>
                {overdue.length > 0 && !result && <button type="button" className="text-[10px] text-blue-600 hover:underline" onClick={() => tickAll(overdue, !overdue.every(r => ticked.has(r.id)))} data-testid="mirror-ir-sweep-toggle-overdue">{overdue.every(r => ticked.has(r.id)) ? "untick all" : "tick all"}</button>}
              </div>
              <div className="border rounded divide-y">
                {overdue.map(r => <Row key={r.id} r={r} />)}
                {waiting.map(r => <Row key={r.id} r={r} hold={r.hold} />)}
                {!overdue.length && !waiting.length && <div className="p-2 text-[11px] text-muted-foreground">No rows at 3rd Chase -IR.</div>}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Unticked by default — these flip Status → Declined. Rows where the broker replied after the final chase are held back.</p>
            </section>
          </div>
        )}
        <AlertDialogFooter>
          {!result ? (
            <>
              <AlertDialogCancel disabled={running} onClick={onClose} data-testid="mirror-ir-sweep-cancel">Cancel</AlertDialogCancel>
              <Button className="bg-red-700 hover:bg-red-800" disabled={running || loading || ticked.size === 0} onClick={run} data-testid="mirror-ir-sweep-run-btn">
                {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Archive className="w-3.5 h-3.5 mr-1" />}
                File {ticked.size} item{ticked.size === 1 ? "" : "s"}
              </Button>
            </>
          ) : (
            <Button onClick={onDone} data-testid="mirror-ir-sweep-done-btn">Close</Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
